import '../../i18n/autoTranslate';
import React, { useMemo, useState } from 'react';
import SpotlightCard from '../../../components/ui/SpotlightCard';
import Icon from '../../../components/AppIcon';
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  RadarChart,
  Radar,
  PolarGrid,
  PolarAngleAxis,
  PolarRadiusAxis,
} from 'recharts';

const safeNum = (v, d = 0) => (Number.isFinite(Number(v)) ? Number(v) : d);

const ComparativeAnalytics = ({ data = {}, skillAnalysis = [] }) => {
  const [view, setView] = useState('baseline');

  const tabs = [
    { value: 'baseline', label: 'Baseline', icon: 'Activity' },
    { value: 'formats', label: 'Formats', icon: 'Layers' },
    { value: 'skills', label: 'Skills', icon: 'Target' },
  ];

  const baselineData = useMemo(() => {
    const b = data?.baseline || {};
    return [
      { label: 'Previous', score: safeNum(b.prevAvg, 0), sessions: safeNum(b.prevCount, 0) },
      { label: 'Recent', score: safeNum(b.recentAvg, 0), sessions: safeNum(b.recentCount, 0) },
    ];
  }, [data]);

  const byType = useMemo(() => {
    const list = Array.isArray(data?.byType) ? data.byType : [];
    return list.map((x) => ({ name: x.name, sessions: safeNum(x.value, 0) }));
  }, [data]);

  const radarData = useMemo(() => {
    const preferred = ['technical_skills', 'communication', 'problem_solving', 'cultural_fit'];
    const map = new Map((skillAnalysis || []).map((s) => [s.key, s]));
    const out = [];
    preferred.forEach((k) => {
      const row = map.get(k);
      if (!row) return;
      out.push({ skill: row.skill, score: safeNum(row.score, 0) });
    });
    if (!out.length) {
      (skillAnalysis || []).slice(0, 6).forEach((s) => out.push({ skill: s.skill, score: safeNum(s.score, 0) }));
    }
    return out;
  }, [skillAnalysis]);

  const CustomTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div className="rounded-2xl border border-white/10 bg-[#080a0f]/90 px-4 py-3 shadow-2xl backdrop-blur-xl">
        <div className="text-sm font-semibold text-white mb-2">{label}</div>
        <div className="space-y-1">
          {payload.map((p, i) => (
            <div key={i} className="flex items-center justify-between gap-6 text-sm">
              <span className="text-white/70">{p.name}</span>
              <span className="text-white font-semibold">{p.value}</span>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const delta = safeNum(data?.baseline?.delta, 0);

  return (
    <SpotlightCard
      className="relative overflow-hidden rounded-3xl border border-white/10 bg-white/[0.04] backdrop-blur-2xl shadow-[0_18px_50px_-22px_rgba(0,0,0,0.75)]"
      spotlightColor="rgba(99,102,241,0.12)"
    >
      <div className="pointer-events-none absolute inset-0">
        <div className="absolute -top-24 -right-24 h-[340px] w-[340px] rounded-full bg-violet-500/12 blur-[90px]" />
        <div className="absolute -bottom-24 -left-24 h-[340px] w-[340px] rounded-full bg-sky-500/10 blur-[90px]" />
        <div className="absolute inset-0 bg-gradient-to-b from-black/25 via-black/10 to-black/45" />
      </div>

      <div className="relative z-10 p-6">
        <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-4">
          <div>
            <div className="text-sm font-semibold text-white">Comparative analytics</div>
            <div className="text-xs text-white/55 mt-1">
              Compare your recent performance to your own baseline, formats, and skill profile.
            </div>
          </div>

          <div className="flex flex-wrap items-center gap-2">
            {tabs.map((t) => {
              const active = view === t.value;
              return (
                <button
                  key={t.value}
                  onClick={() => setView(t.value)}
                  className={[
                    "inline-flex items-center gap-2 rounded-2xl border px-3 py-2 text-xs font-semibold transition",
                    active ? "border-white/14 bg-white/[0.10] text-white" : "border-white/10 bg-white/[0.03] text-white/70 hover:text-white hover:bg-white/[0.06]",
                  ].join(" ")}
                >
                  <Icon name={t.icon} size={14} className={active ? "text-white" : "text-white/70"} />
                  {t.label}
                </button>
              );
            })}
          </div>
        </div>

        {view === 'baseline' ? (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 rounded-3xl border border-white/10 bg-white/[0.03] p-5">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-white">Baseline vs recent</div>
                <div className="text-xs text-white/55">
                  Delta: <span className={delta >= 0 ? "text-emerald-300" : "text-rose-300"}>{delta >= 0 ? "+" : ""}{delta}</span>
                </div>
              </div>

              <div className="mt-4 h-[240px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={baselineData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="label" tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 12 }} axisLine={{ stroke: 'rgba(255,255,255,0.10)' }} tickLine={false} />
                    <YAxis domain={[0, 100]} tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 12 }} axisLine={{ stroke: 'rgba(255,255,255,0.10)' }} tickLine={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="score" name="Avg score" fill="rgba(56,189,248,0.55)" radius={[10, 10, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="mt-3 text-xs text-white/55">
                This compares your last 5 sessions to the 5 sessions before them — a clean signal of momentum.
              </div>
            </div>

            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5">
              <div className="text-sm font-semibold text-white">What to do next</div>
              <div className="text-xs text-white/55 mt-1">Turn the delta into action.</div>

              <div className="mt-4 space-y-3">
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3">
                  <div className="text-xs font-semibold text-white/80">Stabilize structure</div>
                  <div className="text-xs text-white/55 mt-1">Use the same answer template every time to reduce variance.</div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3">
                  <div className="text-xs font-semibold text-white/80">Practice in weak format</div>
                  <div className="text-xs text-white/55 mt-1">If one format lags, train it until the gap closes.</div>
                </div>
                <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3">
                  <div className="text-xs font-semibold text-white/80">Re-test weekly</div>
                  <div className="text-xs text-white/55 mt-1">Do 1 benchmark session/week to track improvements.</div>
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {view === 'formats' ? (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 rounded-3xl border border-white/10 bg-white/[0.03] p-5">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-white">Session volume by format</div>
                <div className="text-xs text-white/55">Practice diversity signal</div>
              </div>

              <div className="mt-4 h-[260px]">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={byType} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                    <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.06)" />
                    <XAxis dataKey="name" tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 12 }} axisLine={{ stroke: 'rgba(255,255,255,0.10)' }} tickLine={false} />
                    <YAxis tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 12 }} axisLine={{ stroke: 'rgba(255,255,255,0.10)' }} tickLine={false} allowDecimals={false} />
                    <Tooltip content={<CustomTooltip />} />
                    <Bar dataKey="sessions" name="Sessions" fill="rgba(99,102,241,0.55)" radius={[10, 10, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>

              <div className="mt-3 text-xs text-white/55">
                A balanced mix improves adaptability. If you avoid one format, it becomes a blind spot.
              </div>
            </div>

            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5">
              <div className="text-sm font-semibold text-white">Recommended mix</div>
              <div className="text-xs text-white/55 mt-1">Simple weekly plan.</div>

              <div className="mt-4 space-y-3 text-xs text-white/70">
                <div className="flex items-center justify-between">
                  <span>Chat drills</span>
                  <span className="text-white/55">2×</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Phone simulation</span>
                  <span className="text-white/55">1×</span>
                </div>
                <div className="flex items-center justify-between">
                  <span>Video session</span>
                  <span className="text-white/55">1×</span>
                </div>
                <div className="pt-3 border-t border-white/10 text-white/55">
                  Adjust based on your target role and interview pipeline.
                </div>
              </div>
            </div>
          </div>
        ) : null}

        {view === 'skills' ? (
          <div className="mt-6 grid grid-cols-1 lg:grid-cols-3 gap-6">
            <div className="lg:col-span-2 rounded-3xl border border-white/10 bg-white/[0.03] p-5">
              <div className="flex items-center justify-between">
                <div className="text-sm font-semibold text-white">Skill profile</div>
                <div className="text-xs text-white/55">Radar from scored sessions</div>
              </div>

              <div className="mt-4 h-[280px]">
                <ResponsiveContainer width="100%" height="100%">
                  <RadarChart data={radarData}>
                    <PolarGrid stroke="rgba(255,255,255,0.08)" />
                    <PolarAngleAxis dataKey="skill" tick={{ fill: 'rgba(255,255,255,0.55)', fontSize: 11 }} />
                    <PolarRadiusAxis domain={[0, 100]} tick={{ fill: 'rgba(255,255,255,0.45)', fontSize: 10 }} />
                    <Radar dataKey="score" name="Score" stroke="rgba(52,211,153,0.9)" fill="rgba(52,211,153,0.22)" />
                    <Tooltip content={<CustomTooltip />} />
                  </RadarChart>
                </ResponsiveContainer>
              </div>

              <div className="mt-3 text-xs text-white/55">
                Grow the smallest axis first — it produces the fastest score improvement.
              </div>
            </div>

            <div className="rounded-3xl border border-white/10 bg-white/[0.03] p-5">
              <div className="text-sm font-semibold text-white">Skill actions</div>
              <div className="text-xs text-white/55 mt-1">What to practice next.</div>

              <div className="mt-4 space-y-3">
                {(skillAnalysis || []).slice().sort((a,b)=>safeNum(a.score,0)-safeNum(b.score,0)).slice(0,3).map((s, idx) => (
                  <div key={idx} className="rounded-2xl border border-white/10 bg-white/[0.02] p-3">
                    <div className="text-xs font-semibold text-white/80">{s.skill}</div>
                    <div className="text-xs text-white/55 mt-1">Do 2 targeted drills + 1 benchmark session.</div>
                    <div className="mt-2 h-2 rounded-full bg-white/8 overflow-hidden">
                      <div className="h-full rounded-full bg-gradient-to-r from-violet-400/70 via-sky-400/70 to-emerald-400/70" style={{ width: `${Math.min(100, Math.max(0, safeNum(s.score, 0)))}%` }} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        ) : null}
      </div>
    </SpotlightCard>
  );
};

export default ComparativeAnalytics;
